import React, { useEffect, useMemo, useState } from "react";
import { canPerform } from "../utils/permissions";
import { EditableField } from "./EditableField";
import { ObjectContext } from "./ObjectContext";
import { PermissionBadge } from "./PermissionBadge";

export function WorkspaceCard({ workspace, role, onUpdate, onAction }) {
  const [activeSectionId, setActiveSectionId] = useState(workspace.sections?.[0]?.id);
  const sections = useMemo(() => workspace.sections || [], [workspace.sections]);
  const section = sections.find((item) => item.id === activeSectionId) || sections[0];
  const canEdit = canPerform(role, "edit", workspace.access);
  const canContribute = canPerform(role, "contribute", workspace.access);
  const canApprove = canPerform(role, "approve", workspace.access);

  useEffect(() => {
    setActiveSectionId(workspace.sections?.[0]?.id);
  }, [workspace.id, workspace.sections]);

  return (
    <article className={`lw-workspace lw-accent--${workspace.accent || "cyan"}`} aria-labelledby={`${workspace.id}-title`} data-edit-name={workspace.title || "Workspace card"}>
      <header className="lw-workspace__header">
        <div>
          <span className="lw-kicker">{workspace.eyebrow || "Workspace"}</span>
          <h2 id={`${workspace.id}-title`}>{workspace.title}</h2>
          <p>{workspace.summary}</p>
        </div>
        <PermissionBadge role={role} workspaceAccess={workspace.access} />
      </header>

      <div className="lw-workspace__layout">
        <ObjectContext object={workspace.object} />

        <section className="lw-detail" aria-label={`${workspace.title} details`}>
          <div className="lw-detail__tabs" role="tablist" aria-label="Workspace sections">
            {sections.map((item) => (
              <button
                type="button"
                role="tab"
                aria-selected={item.id === section?.id}
                className={item.id === section?.id ? "is-active" : ""}
                key={item.id}
                onClick={() => setActiveSectionId(item.id)}
              >
                {item.label}
              </button>
            ))}
          </div>

          <div className="lw-detail__scroll" tabIndex="0" data-deck-interactive="true">
            {section ? (
              <>
                <div className="lw-detail__heading">
                  <div>
                    <span className="lw-kicker">{section.label}</span>
                    <h3>{section.title}</h3>
                  </div>
                  {canContribute ? (
                    <button type="button" className="lw-button lw-button--quiet" onClick={() => onAction?.("contribute", workspace, section)}>
                      Add note or file
                    </button>
                  ) : null}
                </div>
                <p>{section.description}</p>
                <div className="lw-edit-grid">
                  {(section.items || []).map((item, index) => (
                    <EditableField
                      key={`${section.id}-${index}`}
                      label={`Item ${index + 1}`}
                      value={item}
                      canEdit={canEdit}
                      onCommit={(value) => onUpdate?.({ workspaceId: workspace.id, sectionId: section.id, index, value })}
                    />
                  ))}
                </div>
              </>
            ) : (
              <p>No detail sections are configured.</p>
            )}
          </div>

          <footer className="lw-detail__actions">
            <button type="button" className="lw-button lw-button--primary" onClick={() => onAction?.("open", workspace, section)}>
              Open {workspace.title}
            </button>
            {canApprove ? (
              <button type="button" className="lw-button" onClick={() => onAction?.("approve", workspace, section)}>
                Review approval
              </button>
            ) : null}
          </footer>
        </section>
      </div>
    </article>
  );
}
